import { useRef, useState } from 'react';

import { type InfiniteData } from '@tanstack/react-query';

import { useAppDispatch, useAppSelector, useGetGroups, useGroupFilterParams } from '@pkg/hooks';

import {
    EventTypes,
    type GroupDataItem,
    type MutableGetGroupItemsResponse,
} from '@app/client/common/structs/groups/groups';
import config from '@app/config';
import { useSubscribeToTopic, WebsocketTopicIds } from '@app/helpers';
import { reactQueryClient } from '@app/reactQueryClient';
import { animateNftEvent } from '@app/store/slices/pod-events';
import {
    type WebsocketGroupItem,
    type WebsocketMessage,
    type WebsocketPayload,
    type WebsocketPodData,
    WebsocketTopic,
} from '@app/store/types/websockets';

import { useFilteredItems } from './useFilteredItems';
import { groupsCacheKeys } from './utils';

// TODO: React Query implementation should be refactored and this removed

export const useGroupActivity = (groupId: string) => {
    const dispatch = useAppDispatch();

    const [isDisplayed, setIsDisplayed] = useState(false);
    const debounce = useRef({
        podPage: 0,
        podList: 0,
    });
    const filters = useGroupFilterParams();
    const isSpamEnabled = !!filters?.spam;

    const { refetch: refetchAllGroupData } = useGetGroups();
    const { refetch: refetchGroupItems } = useFilteredItems({ groupId, filters });
    const { showEvents } = useAppSelector((state) => state.featureFlags.features);

    function getEventType(topic: WebsocketTopic | null): EventTypes | undefined {
        switch (topic) {
            case WebsocketTopic.GroupNftAdded:
                return EventTypes.Purchase;
        }
    }

    // TODO: This is just a temporary way of calculating the tier of an NFT
    function getAssetTier(item: WebsocketGroupItem) {
        const tier1_limit = 0.02;
        const tier2_limit = 0.2;
        const tier3_limit = 2;

        const purchasePrice = item.item_type.Nft.purchase_price;

        if (!purchasePrice || purchasePrice <= tier1_limit) return 1;
        if (purchasePrice <= tier2_limit) return 2;
        if (purchasePrice <= tier3_limit) return 3;
        if (purchasePrice > tier3_limit) return 4;
        return 1;
    }

    const onmessage = (message) => {
        try {
            const websocketMessage: WebsocketMessage = JSON.parse(message.data);
            const payload = websocketMessage.payload as WebsocketPayload<WebsocketPodData>;

            // If the received data is related to spam contracts
            // and the spam is not enabled - do not display activity button
            if (payload?.data?.item.spam && !isSpamEnabled) return;

            const isGroupTopic = websocketMessage.topic && websocketMessage.topic.startsWith('group-');
            if (!isGroupTopic) return;

            const eventGroupId = websocketMessage.topic.replace('group-', '');

            // Refetch the current group data
            if (eventGroupId && groupId && groupId === eventGroupId) {
                setIsDisplayed(true);

                if (!debounce.current.podPage) {
                    if ((filters?.sort === 'purchase_date' || !filters?.sort) && filters?.dir === 'Desc') {
                        const pageItem = payload?.data?.item as WebsocketGroupItem;
                        const topic = payload?.data?.topic;

                        reactQueryClient.setQueryData(
                            groupsCacheKeys.items(groupId, filters),
                            (data: InfiniteData<MutableGetGroupItemsResponse> | undefined) => {
                                if (data && data?.pages?.[0]?.data.length) {
                                    const pages = structuredClone(data.pages);

                                    // Ignore if spam NFT added when spam filter on
                                    if (pageItem.spam) {
                                        return { ...data, pages };
                                    }

                                    // NFT item added
                                    if (topic === WebsocketTopic.GroupNftAdded) {
                                        // Add item to beggining of the grid
                                        if (
                                            (filters?.sort === 'purchase_date' || !filters?.sort) &&
                                            filters?.dir === 'Desc'
                                        ) {
                                            const eventData = payload?.data as WebsocketPodData;
                                            eventData.item.tier = getAssetTier(
                                                payload?.data?.item as WebsocketGroupItem,
                                            );

                                            // If event type not mapped, no animation will be played
                                            const event = getEventType(payload.data?.topic as WebsocketTopic);
                                            if (showEvents && event) {
                                                dispatch(animateNftEvent({ data: eventData, event }));
                                            }

                                            pages[0].data.unshift(payload?.data as GroupDataItem);
                                            return { ...data, pages };
                                        }
                                    }

                                    return { ...data, pages };
                                }
                            },
                        );
                    } else {
                        debounce.current.podPage = window.setTimeout(() => {
                            refetchGroupItems();
                            debounce.current.podPage = 0;
                        }, config.wsRefreshDelay);
                    }
                }
            } else {
                if (!debounce.current.podList) {
                    debounce.current.podList = window.setTimeout(() => {
                        refetchAllGroupData();
                        debounce.current.podList = 0;
                    }, config.wsRefreshDelay);
                }
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    useSubscribeToTopic({
        id: WebsocketTopicIds.GroupActivity,
        messageKey: 'groupActivityHandler',
        onmessage,
        topic: `${WebsocketTopic.GroupSubscription}${groupId}`,
    });

    return {
        isDisplayed,
        setIsDisplayed,
    };
};
